import React from 'react';
import { newPasswordProblem } from '@turn-connect/shared';

interface NewPasswordFieldsProps {
  password: string;
  confirm: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** Whether the pair is ready to save: meets the account rules and matches. */
export const newPasswordReady = (password: string, confirm: string) => newPasswordProblem(password) === null && password === confirm;

/**
 * "New password" + "Confirm new password" with a live hint for the first
 * rule not yet met. Shared by the set-new-password dialog and the profile.
 */
const NewPasswordFields: React.FC<NewPasswordFieldsProps> = ({
  password,
  confirm,
  onPasswordChange,
  onConfirmChange,
  disabled = false,
  autoFocus = false,
}) => {
  const problem = password ? newPasswordProblem(password) : null;
  const mismatch = confirm.length > 0 && confirm !== password;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div>
        <label htmlFor="new-password" style={labelStyle}>
          New password
        </label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="new-password"
          autoFocus={autoFocus}
          disabled={disabled}
          style={inputStyle}
          data-testid="new-password"
        />
        <div style={hintStyle} data-testid="new-password-hint">
          {problem ?? '8 or more characters, with upper and lower case, a number and a symbol.'}
        </div>
      </div>
      <div>
        <label htmlFor="confirm-new-password" style={labelStyle}>
          Confirm new password
        </label>
        <input
          id="confirm-new-password"
          type="password"
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
          autoComplete="new-password"
          disabled={disabled}
          style={inputStyle}
          data-testid="confirm-new-password"
        />
        {mismatch && (
          <div style={hintStyle} role="alert">
            Passwords don't match.
          </div>
        )}
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--tc-text-secondary, #CCCCCC)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px',
  border: '1px solid var(--tc-border-light, #333333)',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  backgroundColor: 'var(--tc-background-quaternary, #2A2A2A)',
  color: 'var(--tc-text-primary, #FFFFFF)',
};

const hintStyle: React.CSSProperties = {
  marginTop: '6px',
  fontSize: '12px',
  color: 'var(--tc-text-tertiary, #999999)',
};

export default NewPasswordFields;
